import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Collapse,
  Tabs,
  Tab,
  DialogBody,
  Checkbox,
  Colors,
} from "@blueprintjs/core";
import {
  Box,
  CustomButton,
  CustomCard,
  CustomSelect,
  ItemType,
  ThemedDialog,
  TitleComponent,
} from "ui-components";
import {
  FormDataT,
  scheduleOptions,
  defaultData,
} from "../../../utils/constants";
import {
  deepMerge,
  getAddress,
  initializeFormData,
  isEmptyObject,
} from "../../../utils";
import Urls from "./company/urls";
import EmpCount from "./company/emp-count";
import Management from "./config/management";
import ShareAllotMent from "./config/share-allotment";
import { useMutation, useQueries, UseQueryResult } from "react-query";
import { saveConfig } from "ui-io/queries/save-config";
import { updateCompany } from "ui-io/queries/update-company";
import toasts from "../../../utils/toasts";
import { getAdminCompanyById } from "ui-io/queries/get-admin-company-by-id";
import { getCompanyConfig } from "ui-io/queries/get-company-config";
import SearchConfig from "../../../components/search-config";
import { getWebPageHtmlByUrl } from "ui-io/queries/get-web-page-html-by-url";
import CompanyDetails from "./company/company-details";
import { taskAddMutation } from "ui-io/queries/tasks/task-create-mutation";
import { taskRunScrapeConfig } from "ui-io/queries/tasks/task-run-scrape-config";
import styled from "styled-components";
import Addresses from "./company/addresses";
import SearchCompanyWithSic from "../../../components/search-company-with-sic";
import SearchSicWithName from "./company/search-sic-with-name";
import { ConfigT } from "ui-io/config-io";
export type HtmlResponse = { html: string };
export type HtmlRequest = { url: string };

const ConfigurationCreate = ({ companyID }: { companyID?: string }) => {
  const [tagValues, setTagValues] = useState<string[]>([]);
  const [categoryValues, setCategoryValues] = useState<string[]>([]);
  const [companyIdBySic, setCompanyIdBySic] = useState<string>("");
  const [badgeValues, setBadgeValues] = useState<(string | null)[]>([]);
  const [isUrlExpanded, setUrlExpanded] = useState(false);
  const [formData, setFormData] = useState<FormDataT>(defaultData);
  const [isShareExpanded, setShareExpanded] = useState<boolean>(false);
  const [showTaskDialog, setShowTaskDialog] = useState<boolean>(false);
  const [isFastLane, setIsFastLane] = useState<boolean>(false);
  const [type, setType] = useState<string>("MANUAL");
  const [taskCreateChecked, setTaskCreateChecked] = useState<boolean>(false);
  const [runScrapeChecked, setRunScrapeChecked] = useState<boolean>(false);
  const [selectedUserType, setSelectedUserType] = useState<string[]>([
    "MANUAL",
  ]);
  const USER_TYPE_OPTIONS: ItemType[] = [
    { label: "Manual", value: "MANUAL" },
    { label: "Web Scrape Config", value: "WEB_SCRAPE_CONFIG" },
  ];

  const [companyDetail, companyConfig] = useQueries([
    { ...getAdminCompanyById({ companyID }), enabled: !!companyID },
    { ...getCompanyConfig({ companyID }), enabled: !!companyID },
  ]);

  const updateCompanyMutation = useMutation(updateCompany, {
    onSuccess: async () => {
      toasts.success("Successfully updated");
      if (taskCreateChecked) setShowTaskDialog(true);
      if (runScrapeChecked) {
        // runScrapeConfigMutation.mutate(generateConfigRequestBody);
      }
    },

    onError: () => {
      toasts.error("Could not update");
    },
  });
  const saveConfigMutation = useMutation(saveConfig, {
    onSuccess: () => {},
    onError: () => {},
  });
  const getHtmlByUrlMutation = useMutation(getWebPageHtmlByUrl);

  const changeFormData = (name: string, value: any) => {
    const keys = name.split(".");
    const setNestedValue = (obj: any, keys: string[], value: any): any => {
      if (keys.length === 1) {
        return { ...obj, [keys[0]]: value };
      }
      return {
        ...obj,
        [keys[0]]: setNestedValue(obj?.[keys[0]] ?? {}, keys.slice(1), value),
      };
    };

    setFormData((prev) => {
      return setNestedValue(prev, keys, value);
    });
  };
  const handleOnChange = (
    event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    changeFormData(name, value);
  };

  const handleOpenWebsite = (url: string) => {
    if (url) getHtmlByUrlMutation.mutate({ url: url });
  };
  const handleShareScheduleChange = (index: number, value: string) => {
    changeFormData(
      `configDetail.share_allotment.${index}.schedule.cron`,
      value,
    );
  };
  const handleManagementScheduleChange = (value: string) => {
    changeFormData(`configDetail.management.schedule.cron`, value);
  };

  useEffect(() => {
    initializeFormData(
      companyDetail.data,
      companyConfig.data,
      setFormData,
      setTagValues,
      setBadgeValues,
      setCategoryValues,
    );
  }, [companyDetail.isSuccess, companyConfig.isSuccess]);

  useEffect(() => {
    if (formData.companyDetail.licence_no) {
      handleOpenWebsite(
        `https://find-and-update.company-information.service.gov.uk/company/${formData.companyDetail.licence_no}`,
      );
    }
  }, [formData.companyDetail.licence_no]);

  const isEmptyExceptStatementType = (item: any): boolean => {
    const { statement_type, ...rest } = item;
    return Object.values(rest).every((val) => {
      if (val === null || val === "") return true;
      if (typeof val === "object") return isEmptyExceptStatementType(val);
      return false;
    });
  };

  const filteredShareAllotment = Object.values(
    formData?.configDetail?.share_allotment,
  ).filter((item) => !isEmptyExceptStatementType(item));
  const modifiedEmpCount = () => {
    const empCountArray = Array.isArray(formData.companyDetail.emp_count)
      ? [...formData.companyDetail.emp_count].sort(
          (a, b) => Number(a.year) - Number(b.year),
        )
      : [];
    let resultantArray = [];
    for (let index = 0; index < empCountArray.length; index++) {
      const currYearRow = empCountArray[index];
      if (
        index != 0 &&
        Number(empCountArray[index - 1].year) ===
          Number(empCountArray[index].year) - 1
      ) {
        currYearRow.count.from = empCountArray[index - 1].count.to;
      }
      if (currYearRow.year !== "")
        resultantArray.push({
          ...currYearRow,
          count: {
            from: Number(currYearRow.count.from),
            to: Number(currYearRow.count.to),
          },
        });
    }
    return resultantArray;
  };
  const mergeWithAnotherData = (otherDataQuery: ConfigT) => {
    let merged: FormDataT = { ...formData };

    if (otherDataQuery) {
      const configDetail = otherDataQuery;

      if (merged.configDetail.share_allotment && configDetail.share_allotment) {
        merged.configDetail["share_allotment"] = [
          ...configDetail.share_allotment,
        ] as any;
      }
      if (merged.configDetail && configDetail.management)
        merged.configDetail["management"] = {
          ...configDetail.management,
        } as any;
    }

   
    setFormData(merged);
    return merged;
  };

  const getModifitedTags = () => {
    return formData.companyDetail.tags;
  };
  //task creation
  const taskCreateMutation = useMutation(taskAddMutation, {
    onError: (error) => {
      toasts.error(`${error}`);
    },
    onSuccess: async () => {
      toasts.success("Successfully Task Created");
    },
  });

  const runScrapeConfigMutation = useMutation(taskRunScrapeConfig, {
    onError: (error) => {
      toasts.error(`Run scrape failed: ${error}`);
    },
    onSuccess: async () => {
      toasts.success("Run scrape config executed successfully");
    },
  });
  const generateConfigRequestBody = {
    uuid: formData.configDetail.companyID,
    companyID: formData.configDetail.companyID,
    licence_no: formData.configDetail.licence_no,
    name: formData.configDetail.name,
    status: formData.configDetail.status,
    // body: {
    share_allotment: Object.values(filteredShareAllotment),
    ...(isEmptyObject(formData.configDetail.management)
      ? undefined
      : { management: formData.configDetail.management }),
    // },
  };
 
  return (
    <>
      <Box flexDir="row" justify="between">
        <TitleComponent marginTop="0px">Create Configuration</TitleComponent>
        <SearchSicWithName
          setCompanyIdBySic={setCompanyIdBySic}
          setFormData={setFormData}
        />
      </Box>
      <Box
        flexDir="row"
        items="start"
        justify="between"
        gap="small"
        mt={"large"}
      >
        <Box width={"1/3"}>
          <CustomCard marginTop={"0px"}>
            <Box width={"auto"} p={"medium"}>
              <Box
                mt={"0"}
                mb={"small"}
                mr={"xsmall"}
                flex="true"
                justify="between"
                gap={"medium"}
                items="center"
              >
                <Box flexDir="row" gap="small" justify="center" mt={"0"}>
                  <Box flexDir="row" items="center" gap="small">
                    <TitleComponent
                      fontSize="14px"
                      marginTop="0px"
                      marginBottom="0px"
                    >
                      Create Task
                    </TitleComponent>
                    <Checkbox
                      checked={taskCreateChecked && !runScrapeChecked}
                      onChange={() => {
                        if (!runScrapeChecked) {
                          setTaskCreateChecked(!taskCreateChecked);
                        }
                      }}
                      disabled={runScrapeChecked}
                      className="mb-none"
                    />
                  </Box>
                  <Box flexDir="row" items="center" gap="small">
                    <TitleComponent
                      fontSize="14px"
                      marginTop="0px"
                      marginBottom="0px"
                    >
                      Run Scrape
                    </TitleComponent>
                    <Checkbox
                      checked={runScrapeChecked}
                      onChange={() => {
                        setRunScrapeChecked(!runScrapeChecked);
                        if (!runScrapeChecked) {
                          setTaskCreateChecked(false);
                        }
                      }}
                      className="mb-none"
                    />
                  </Box>
                </Box>
                <CustomButton
                  width="100%"
                  intent="primary"
                  onClick={() => {
                    saveConfigMutation.mutate(generateConfigRequestBody);
                    updateCompanyMutation.mutate({
                      companyID: formData.companyDetail.companyID,
                      body: {
                        ...formData.companyDetail,
                        description: formData.companyDetail.description,
                        tags: getModifitedTags(),
                        emp_count: modifiedEmpCount(),
                        url: formData.companyDetail.url,
                        industry: formData.companyDetail.industry,
                        address: getAddress(formData.companyDetail.addresses),
                        badges: formData.companyDetail.badges,
                        company_type: formData.companyDetail.company_type,
                      },
                    });
                  }}
                >
                  Save
                </CustomButton>
              </Box>
              <BorderDiv />
              <Addresses
                addresses={formData.companyDetail.addresses}
                changeFormData={changeFormData}
              />
              <EmpCount
                emp_count={formData.companyDetail.emp_count}
                changeFormData={changeFormData}
              />
              <Urls
                url={formData.companyDetail.url}
                isUrlExpanded={isUrlExpanded}
                setUrlExpanded={setUrlExpanded}
                handleOnChange={handleOnChange}
                handleOpenWebsite={handleOpenWebsite}
              />
            </Box>
            <Box width={"auto"} p={"medium"}>
              <Box flexDir="row" items="center" mb={"medium"}>
                <SearchConfig
                  placeholder="copy from"
                  mergeWithAnotherData={mergeWithAnotherData}
                />
              </Box>
              <CustomButton
                className="mb-sm"
                onClick={() => setShareExpanded(!isShareExpanded)}
                icon={isShareExpanded ? "chevron-down" : "chevron-right"}
              >
                Share Allotment
              </CustomButton>
              {/* share expand button starts here */}
              <Collapse isOpen={isShareExpanded}>
                <Box ml={"small"}>
                  <Tabs id="share_allotment">
                    {Array.isArray(formData?.configDetail.share_allotment) &&
                      formData?.configDetail.share_allotment?.map(
                        (formItem, index) => {
                          return (
                            <Tab
                              key={index}
                              id={`${index}`}
                              title={formItem?.statement_type?.toUpperCase()}
                              panel={
                                <ShareAllotMent
                                  idx={index}
                                  share={formItem}
                                  handleShareScheduleChange={
                                    handleShareScheduleChange
                                  }
                                  handleOnChange={handleOnChange}
                                />
                              }
                              panelClassName="ember-panel"
                            />
                          );
                        },
                      )}
                  </Tabs>
                </Box>
              </Collapse>
              {/* share expand button ends here */}
              {/* management starts from here */}
              <Management
                handleManagementScheduleChange={handleManagementScheduleChange}
                management={formData.configDetail.management}
                scheduleOptions={scheduleOptions}
                handleOnChange={handleOnChange}
                handleOpenWebsite={handleOpenWebsite}
              />
            </Box>
          </CustomCard>
        </Box>
        <Box width={"2/3"}>
          <CompanyDetails
            companyID={companyID}
            companyDetails={formData.companyDetail}
            tagValues={tagValues}
            setTagValues={setTagValues}
            categoryValues={categoryValues}
            setCategoryValues={setCategoryValues}
            changeFormData={changeFormData}
            getHtmlByUrlMutation={getHtmlByUrlMutation}
            handleOnChange={handleOnChange}
            badgeValues={badgeValues}
            setBadgeValues={setBadgeValues}
          />
        </Box>
      </Box>
      <ThemedDialog
        isOpen={showTaskDialog}
        onClose={() => setShowTaskDialog(false)}
        title="Create Task"
      >
        <DialogBody>
          <TitleComponent fontSize="14px" marginTop="0px">
            Do you want to create a task related to this change?
          </TitleComponent>
          <Box flexDir="row" gap={"medium"} mt={"medium"} justify="between">
            <Box flexDir="row" items="center" gap={"medium"}>
              <Checkbox
                checked={isFastLane}
                onChange={(e) => setIsFastLane(!isFastLane)}
                label={"Fast Lane"}
                className="mb-none"
              />
              <CustomSelect
                label={
                  USER_TYPE_OPTIONS.find(
                    (item) => item.value === selectedUserType[0],
                  )?.label ?? "Select User Type"
                }
                isActiveItem={true}
                items={USER_TYPE_OPTIONS}
                values={selectedUserType}
                onSelect={(item: ItemType) => {
                  setSelectedUserType([item.value]);
                  setType(item.value);
                }}
              />
            </Box>
            <Box flexDir="row" gap={"small"}>
              <CustomButton
                intent="primary"
                onClick={() => {
                  taskCreateMutation.mutate({
                    body: {
                      companyID: formData.companyDetail.companyID,
                      taskId: "",
                      isFastlaneTask: isFastLane,
                      assignee: isFastLane ? "sys_user" : "",
                      current_state: isFastLane ? "APPROVED" : "PENDING",
                      tags: formData.companyDetail.tags,
                      type: type,
                      configuration: generateConfigRequestBody,
                      oldCompanyDetail: companyDetail.data,
                    },
                  });
                  setShowTaskDialog(false);
                }}
              >
                Yes
              </CustomButton>
              <CustomButton
                onClick={() => {
                  setShowTaskDialog(false);
                  setType("MANUAL");
                  setIsFastLane(false);
                }}
              >
                No
              </CustomButton>
            </Box>
          </Box>
        </DialogBody>
      </ThemedDialog>
    </>
  );
};

export default ConfigurationCreate;

const BorderDiv = styled.div`
  border-bottom: ${({ theme: { darkMode } }) =>
    `1px solid ${darkMode ? Colors.DARK_GRAY5 : Colors.LIGHT_GRAY1}`};
  margin: 14px -35px 20px -35px;
`;
